import { useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { FormattedMessage } from 'react-intl';
import { Box, Pagination } from 'components/ui';
import { TitleTypography } from 'components/styles_components/typography';
import { DividerContainer } from 'components/styles_components/container';
import { useSpace, getStoredSpaceId } from 'contexts/space';
import { useError } from 'contexts/error';
import { usePaginate } from 'contexts/paginate';
import { DashboardLink, hardRedirect } from 'utils/link';
import SpacesQuery from 'queries/space';
import Index from 'components/space/index';
import CreateButton from 'components/space/button/create';
import LoaderContainer from 'components/layout/loader_container';

const IndexContainer = ({ children }) => {
  const { selectSpace } = useSpace();
  const { page, onPageChange } = usePaginate();
  const setError = useError();

  const {
    data = {
      spaces: { spaces: [], spacesTotal: 0, spacesTotalPages: 0 },
      user: { canAddSpace: false },
    },
    loading,
  } = useQuery(SpacesQuery, {
    variables: { page },
    fetchPolicy: 'network-only',
    onError: () => setError('error.space.index'),
  });

  const spaces = data.spaces.spaces;

  useEffect(() => {
    const storedId = getStoredSpaceId();
    if (storedId && spaces.some((s) => s.id === storedId)) {
      selectSpace(storedId);
      hardRedirect(DashboardLink());
    }
  }, [spaces, selectSpace]);

  const handleSelect = useCallback(
    (space) => {
      selectSpace(space.id);
      hardRedirect(DashboardLink());
    },
    [selectSpace]
  );

  return (
    <LoaderContainer loading={loading} error={null} wrap={false} keepRenderedContent={true}>
      <TitleTypography variant="h1">
        <FormattedMessage id="spaces.select" />
      </TitleTypography>
      <Index spaces={data.spaces} loading={loading} onSelect={handleSelect} />
      {data.user.canAddSpace && <CreateButton />}
      <Box display="flex" justifyContent="center">
        {data.spaces.spacesTotalPages > 1 && (
          <Pagination
            count={data.spaces.spacesTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
      <DividerContainer />
      {children}
    </LoaderContainer>
  );
};

export default IndexContainer;
